import React, { useState } from 'react';
import { Activity, Monitor, Smartphone, Zap } from 'lucide-react';

interface PageSpeedResult {
  url: string;
  performance_score: number;
  accessibility_score: number;
  best_practices_score: number;
  seo_score: number;
  mobile_performance_score?: number;
  desktop_performance_score?: number;
  desktop_data?: any;
  mobile_data?: any;
  prospect_id?: string;
  created_at?: string;
  metrics?: {
    first_contentful_paint: number;
    largest_contentful_paint: number;
    cumulative_layout_shift: number;
    speed_index: number;
  };
}

interface PageSpeedAnalysisCardProps {
  pagespeedResults: PageSpeedResult | null;
}

const PageSpeedAnalysisCard: React.FC<PageSpeedAnalysisCardProps> = ({ pagespeedResults }) => {
  const [activeTab, setActiveTab] = useState<'mobile' | 'desktop'>('mobile');

  const getScoreColor = (score: number) => {
    const scorePercentage = Math.round(score * 100);
    if (scorePercentage >= 90) return 'text-green-600 bg-green-50 border-green-200';
    if (scorePercentage >= 70) return 'text-blue-600 bg-blue-50 border-blue-200';
    if (scorePercentage >= 50) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-purple-600 bg-purple-50 border-purple-200';
  };
  
  // Convertir les scores décimaux (0-1) en pourcentages entiers (0-100)
  const formatScore = (score: number): number => {
    return Math.round(score * 100);
  };

  // Utilisation de getScoreLabel pour l'affichage du tooltip (utilisé dans le title des éléments)
  const getScoreLabel = (score: number) => {
    if (score >= 90) return 'Excellent';
    if (score >= 50) return 'Moyen';
    return 'Faible';
  };

  const formatMetricValue = (value: number, metric: string) => {
    if (metric === 'cumulative_layout_shift') return value.toFixed(2);
    return value.toFixed(1) + 's';
  };

  if (!pagespeedResults) {
    return null;
  }

  // Fonction d'extraction des scores robuste
  const extractScore = (data: any, field: string, defaultValue: number = 0): number => {
    // Vérifie d'abord les scores spécifiques
    if (data[field] !== undefined && data[field] !== null) {
      return data[field];
    }
    
    // Ensuite vérifie dans les données brutes (par exemple mobile_data ou desktop_data)
    const rawDataField = field.startsWith('mobile_') ? 'mobile_data' : 'desktop_data';
    const baseField = field.replace(/^(mobile_|desktop_)/, '');
    
    if (data[rawDataField] && 
        data[rawDataField].lighthouseResult && 
        data[rawDataField].lighthouseResult.categories) {
      // Pour les scores de performance
      if (baseField === 'performance_score' && 
          data[rawDataField].lighthouseResult.categories.performance && 
          typeof data[rawDataField].lighthouseResult.categories.performance.score === 'number') {
        return data[rawDataField].lighthouseResult.categories.performance.score;
      }
      
      // Pour les autres métriques (accessibilité, SEO, etc.)
      const categoryMap: {[key: string]: string} = {
        'accessibility_score': 'accessibility',
        'best_practices_score': 'best-practices',
        'seo_score': 'seo'
      };
      
      const category = categoryMap[baseField];
      if (category && 
          data[rawDataField].lighthouseResult.categories[category] && 
          typeof data[rawDataField].lighthouseResult.categories[category].score === 'number') {
        return data[rawDataField].lighthouseResult.categories[category].score;
      }
    }
    
    // Fallback sur la valeur par défaut
    return defaultValue;
  };
  
  // Détermine si nous avons des données mobile et desktop
  const hasMobileData = pagespeedResults.mobile_data || pagespeedResults.mobile_performance_score;
  const hasDesktopData = pagespeedResults.desktop_data || pagespeedResults.desktop_performance_score;
  
  // Pré-calcul des scores pour éviter les répétitions dans le rendu
  const mobilePerformanceScore = extractScore(pagespeedResults, 'mobile_performance_score', pagespeedResults.performance_score);
  const desktopPerformanceScore = extractScore(pagespeedResults, 'desktop_performance_score', pagespeedResults.performance_score);
  const activeTabPerformanceScore = activeTab === 'mobile' ? mobilePerformanceScore : desktopPerformanceScore;
  
  // Extraction des autres scores
  const mobileAccessibilityScore = extractScore(pagespeedResults, 'accessibility_score', pagespeedResults.accessibility_score);
  const desktopAccessibilityScore = extractScore(pagespeedResults, 'accessibility_score', pagespeedResults.accessibility_score);
  const activeTabAccessibilityScore = activeTab === 'mobile' ? mobileAccessibilityScore : desktopAccessibilityScore;
  
  const mobileBestPracticesScore = extractScore(pagespeedResults, 'best_practices_score', pagespeedResults.best_practices_score);
  const desktopBestPracticesScore = extractScore(pagespeedResults, 'best_practices_score', pagespeedResults.best_practices_score);
  const activeTabBestPracticesScore = activeTab === 'mobile' ? mobileBestPracticesScore : desktopBestPracticesScore;
  
  const mobileSeoScore = extractScore(pagespeedResults, 'seo_score', pagespeedResults.seo_score);
  const desktopSeoScore = extractScore(pagespeedResults, 'seo_score', pagespeedResults.seo_score);
  const activeTabSeoScore = activeTab === 'mobile' ? mobileSeoScore : desktopSeoScore;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Zap className="w-4 h-4 text-blue-500 mr-2" />
          <span className="text-sm font-medium text-gray-700">Analyse PageSpeed</span>
        </div>
        <span className="text-xs text-gray-500">
          {pagespeedResults.created_at ? new Date(pagespeedResults.created_at).toLocaleDateString('fr-FR') : 'Date inconnue'}
        </span>
      </div>

      {/* Tabs pour mobile/desktop */}
      {hasMobileData && hasDesktopData && (
        <div className="flex space-x-2 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('mobile')}
            className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${activeTab === 'mobile' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Smartphone className="w-4 h-4 mr-1" />
            Mobile
          </button>
          <button
            onClick={() => setActiveTab('desktop')}
            className={`flex items-center px-3 py-2 text-sm font-medium transition-colors ${activeTab === 'desktop' ? 'text-blue-600 border-b-2 border-blue-500' : 'text-gray-500 hover:text-gray-700'}`}
          >
            <Monitor className="w-4 h-4 mr-1" />
            Desktop
          </button>
        </div>
      )}

      {/* Section des scores */}
      <div className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-md">
        <div className="grid grid-cols-4 divide-x divide-gray-100">
          {/* Score de performance */}
          <div className="p-3 text-center">
            <div className="flex items-center space-x-2">
              <div className="h-20 w-20 rounded-full border flex items-center justify-center shadow-sm relative">
                <div className={`absolute inset-0 rounded-full ${getScoreColor(activeTabPerformanceScore)} opacity-10`}></div>
                <span className="text-xl font-bold">
                  {formatScore(activeTabPerformanceScore)}
                </span>
              </div>
            </div>
            <p className="text-xs font-medium text-gray-600">Performance</p>
          </div>

          {/* Score d'accessibilité */}
          <div className="p-3 text-center">
            <div className="mb-1">
              <div 
                className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center shadow-sm border ${getScoreColor(activeTabAccessibilityScore)}`}
                title={`Accessibilité: ${getScoreLabel(activeTabAccessibilityScore)}`}
              >
                <span className="text-lg font-bold">
                  {formatScore(activeTabAccessibilityScore)}
                </span>
              </div>
            </div>
            <p className="text-xs font-medium text-gray-600">Accessibilité</p>
          </div>

          {/* Score de meilleures pratiques */}
          <div className="p-3 text-center">
            <div className="mb-1">
              <div 
                className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center shadow-sm border ${getScoreColor(activeTabBestPracticesScore)}`}
                title={`Meilleures pratiques: ${getScoreLabel(activeTabBestPracticesScore)}`}
              >
                <span className="text-lg font-bold">
                  {formatScore(activeTabBestPracticesScore)}
                </span>
              </div>
            </div>
            <p className="text-xs font-medium text-gray-600">Meilleures pratiques</p>
          </div>

          {/* Score SEO */}
          <div className="p-3 text-center">
            <div className="mb-1">
              <div 
                className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center shadow-sm border ${getScoreColor(activeTabSeoScore)}`}
                title={`SEO: ${getScoreLabel(activeTabSeoScore)}`}
              >
                <span className="text-lg font-bold">
                  {formatScore(activeTabSeoScore)}
                </span>
              </div>
            </div>
            <p className="text-xs font-medium text-gray-600">SEO</p>
          </div>
        </div>
      </div>

      {/* Métriques clés */}
      {pagespeedResults.metrics && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
          <h3 className="text-xs font-semibold text-blue-800 mb-3 flex items-center">
            <Activity className="w-4 h-4 mr-1" />
            Métriques Core Web Vitals
          </h3>
          
          <div className="grid grid-cols-2 gap-4">
            {/* First Contentful Paint */}
            <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-50">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">First Contentful Paint</span>
                <span className="text-sm font-semibold text-gray-800">
                  {formatMetricValue(pagespeedResults.metrics.first_contentful_paint, 'first_contentful_paint')}
                </span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 rounded overflow-hidden">
                <div 
                  className={`h-full ${pagespeedResults.metrics.first_contentful_paint < 2 ? 'bg-green-500' : pagespeedResults.metrics.first_contentful_paint < 4 ? 'bg-yellow-500' : 'bg-red-500'}`} 
                  style={{ width: `${Math.min(100, (pagespeedResults.metrics.first_contentful_paint / 5) * 100)}%` }}
                ></div>
              </div>
            </div>
            
            {/* Largest Contentful Paint */}
            <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-50">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Largest Contentful Paint</span>
                <span className="text-sm font-semibold text-gray-800">
                  {formatMetricValue(pagespeedResults.metrics.largest_contentful_paint, 'largest_contentful_paint')}
                </span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 rounded overflow-hidden">
                <div 
                  className={`h-full ${pagespeedResults.metrics.largest_contentful_paint < 2.5 ? 'bg-green-500' : pagespeedResults.metrics.largest_contentful_paint < 4 ? 'bg-yellow-500' : 'bg-red-500'}`} 
                  style={{ width: `${Math.min(100, (pagespeedResults.metrics.largest_contentful_paint / 6) * 100)}%` }}
                ></div>
              </div>
            </div>
            
            {/* Cumulative Layout Shift */}
            <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-50">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Cumulative Layout Shift</span>
                <span className="text-sm font-semibold text-gray-800">
                  {formatMetricValue(pagespeedResults.metrics.cumulative_layout_shift, 'cumulative_layout_shift')}
                </span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 rounded overflow-hidden">
                <div 
                  className={`h-full ${pagespeedResults.metrics.cumulative_layout_shift < 0.1 ? 'bg-green-500' : pagespeedResults.metrics.cumulative_layout_shift < 0.25 ? 'bg-yellow-500' : 'bg-red-500'}`} 
                  style={{ width: `${Math.min(100, (pagespeedResults.metrics.cumulative_layout_shift / 0.5) * 100)}%` }}
                ></div>
              </div>
            </div>
            
            {/* Speed Index */}
            <div className="bg-white p-3 rounded-lg shadow-sm border border-blue-50">
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Speed Index</span>
                <span className="text-sm font-semibold text-gray-800">
                  {formatMetricValue(pagespeedResults.metrics.speed_index, 'speed_index')}
                </span>
              </div>
              <div className="mt-2 h-2 bg-gray-200 rounded overflow-hidden">
                <div 
                  className={`h-full ${pagespeedResults.metrics.speed_index < 3.4 ? 'bg-green-500' : pagespeedResults.metrics.speed_index < 5.8 ? 'bg-yellow-500' : 'bg-red-500'}`} 
                  style={{ width: `${Math.min(100, (pagespeedResults.metrics.speed_index / 10) * 100)}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageSpeedAnalysisCard;